import React, {type JSX} from 'react';
import {Button, Container, Typography} from '@mui/material';
import {styled} from '@mui/material/styles';
import {useReferenceCode} from '../hooks';
import ReferenceCodeForm from './referenceCodeForm';

const LINK_CONTACT_US = 'https://support.healthbankone.com/';

const StyledContainer = styled(Container)(({theme}) => ({
  '&.MuiContainer-root': {
    backgroundColor: theme.palette.background.paper,
    borderRadius: '16px',
    boxShadow:
      '0px 8px 10px -5px rgba(0, 0, 0, 0.16), 0px 16px 24px 2px rgba(0, 0, 0, 0.1), 0px 6px 30px 5px rgba(0, 0, 0, 0.08)',
  },
}));

const FormContainer = styled(StyledContainer)(({theme}) => ({
  '&.MuiContainer-root': {
    padding: theme.spacing(5, 8, 3, 8),
  },
}));

const AttemptReachedContainer = styled(StyledContainer)(({theme}) => ({
  '&.MuiContainer-root': {
    padding: theme.spacing(5, 8, 5, 8),
    textAlign: 'center',
  },
}));

const ReferenceCodeFormWrapper = styled('div')(({theme}) => ({
  padding: theme.spacing(5, 0, 5.5, 0),
}));

const AttemptsReachedDescriptionWrapper = styled(Typography)(({theme}) => ({
  padding: theme.spacing(1, 0, 5, 0),
}));

const ContactSupportButton = styled(Button)(() => ({
  '&.MuiButton-root': {
    height: '68px',
    width: '200px',
    padding: 0,
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '24px',
  },
}));

function ReferenceCodeCard(): JSX.Element {
  const {
    errorMessage,
    setErrorMessage,
    remainingAttempts,
    authorizeReferenceCode,
    isAuthorizing,
  } = useReferenceCode();

  const getAttemptsReachedMessage = (remainingAttempts: number) =>
    `You have ${
      remainingAttempts === 1 ? '1 attempt' : `${remainingAttempts} attempts`
    } to enter the code`;

  return remainingAttempts === 0 ? (
    <AttemptReachedContainer maxWidth="md">
      <Typography component="h4" variant="h5" align="center">
        Attempts limit reached
      </Typography>
      <AttemptsReachedDescriptionWrapper variant="body4" align="center">
        You have reached your limit of attempts to enter the reference code.
        <br />
        Please try again in 15 minutes or contact AllClear ID support.
      </AttemptsReachedDescriptionWrapper>
      <ContactSupportButton
        variant="contained"
        color="primary"
        onClick={() => window.open(LINK_CONTACT_US, '_blank', 'noopener')}
      >
        Contact support
      </ContactSupportButton>
    </AttemptReachedContainer>
  ) : (
    <FormContainer maxWidth="md">
      <Typography component="h4" variant="h5" align="center">
        Please enter the reference code from the patient record order.
      </Typography>
      <ReferenceCodeFormWrapper>
        <ReferenceCodeForm
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          authorizeReferenceCode={authorizeReferenceCode}
          isAuthorizing={isAuthorizing}
        />
      </ReferenceCodeFormWrapper>
      <Typography align="center">
        {getAttemptsReachedMessage(remainingAttempts)}
      </Typography>
    </FormContainer>
  );
}

export default ReferenceCodeCard;
